/**
 * Painter: turns pointer positions into draw ops, applies them optimistically,
 * batches them to POST /v1/draw, and keeps the session undo stack (spec §2.2).
 */
import { drawArea, pixelFromLatLng, tileFromToken, tileKey, tileToken, type TileRef } from '@drawearth/geo';
import type { DrawOp, InkState } from '@drawearth/shared';
import { ApiError, type ApiClient } from './api.ts';
import type { Position } from './store.ts';
import type { TileStore } from './tiles.ts';

export interface PainterEvents {
  onInk(ink: InkState): void;
  onError(code: string, message?: string): void;
}

interface UndoEntry {
  tile: string;
  idx: number;
  prevColour: number;
}

export class Painter {
  private area = new Map<string, Set<number>>();
  private areaPos: { lat: number; lng: number; radius: number } | null = null;
  private pending = new Map<string, Map<number, number>>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private inflight = false;
  private undoStack: UndoEntry[] = [];
  position: Position | null = null;
  radiusM = 75;
  tzOffsetMinutes = -new Date().getTimezoneOffset();
  flushDelayMs = 250;
  maxOpsPerRequest = 200;

  constructor(
    private api: ApiClient,
    private store: TileStore,
    private events: PainterEvents,
  ) {}

  setPosition(p: Position | null): void {
    this.position = p;
    if (!p) {
      this.area.clear();
      this.areaPos = null;
      return;
    }
    // Recompute the draw area only when the position moved noticeably.
    if (this.areaPos && this.areaPos.radius === this.radiusM && Math.abs(this.areaPos.lat - p.lat) < 2e-6 && Math.abs(this.areaPos.lng - p.lng) < 2e-6) return;
    this.areaPos = { lat: p.lat, lng: p.lng, radius: this.radiusM };
    this.area = new Map();
    for (const [tok, idxs] of drawArea(p.lat, p.lng, this.radiusM)) this.area.set(tok, new Set(idxs));
  }

  /** Tokens of tiles intersecting the draw area. */
  areaTiles(): string[] {
    return [...this.area.keys()];
  }

  inArea(token: string, idx: number): boolean {
    return this.area.get(token)?.has(idx) ?? false;
  }

  /** Paint the pixel under a lat/lng. Returns false when outside the draw area. */
  paintAt(lat: number, lng: number, colour: number): boolean {
    const px = pixelFromLatLng(lat, lng);
    const token = tileToken(px.tile);
    if (!this.inArea(token, px.idx)) return false;
    const current = this.store.colourAt(px.tile, px.idx);
    if (current === colour) return true;
    const prev = this.pending.get(token)?.get(px.idx);
    if (prev === undefined) this.undoStack.push({ tile: token, idx: px.idx, prevColour: current });
    let ops = this.pending.get(token);
    if (!ops) {
      ops = new Map();
      this.pending.set(token, ops);
    }
    ops.set(px.idx, colour);
    this.store.applyLocal(px.tile, [{ idx: px.idx, colour }]);
    this.schedule();
    return true;
  }

  pendingCount(): number {
    let n = 0;
    for (const m of this.pending.values()) n += m.size;
    return n;
  }

  private schedule(): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, this.flushDelayMs);
  }

  /** Send pending ops, one request per tile (spec §4.4). */
  async flush(): Promise<void> {
    if (this.inflight) {
      this.schedule();
      return;
    }
    const pos = this.position;
    if (!pos || this.pending.size === 0) return;
    this.inflight = true;
    try {
      for (const [token, opsMap] of [...this.pending]) {
        const all = [...opsMap].map(([idx, colour]) => ({ idx, colour }));
        this.pending.delete(token);
        for (let i = 0; i < all.length; i += this.maxOpsPerRequest) {
          const ops = all.slice(i, i + this.maxOpsPerRequest);
          await this.send(token, ops, pos);
        }
      }
    } finally {
      this.inflight = false;
      if (this.pending.size) this.schedule();
    }
  }

  private async send(token: string, ops: DrawOp[], pos: Position): Promise<void> {
    try {
      const res = await this.api.draw({ tile: token, lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy, mockFlag: pos.mock, ops, tzOffsetMinutes: this.tzOffsetMinutes });
      this.events.onInk(res.ink);
    } catch (err) {
      const ref = tileFromToken(token);
      await this.store.reload(ref);
      // Drop undo entries for the rejected ops.
      const rejected = new Set(ops.map((o) => o.idx));
      this.undoStack = this.undoStack.filter((u) => !(u.tile === token && rejected.has(u.idx)));
      if (err instanceof ApiError) this.events.onError(err.code, err.message);
      else this.events.onError('network');
    }
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /** Undo the most recent pixel you painted this session. */
  async undo(): Promise<boolean> {
    const last = this.undoStack.pop();
    if (!last) return false;
    await this.flush();
    const ref = tileFromToken(last.tile);
    try {
      const res = await this.api.undo(last.tile, last.idx);
      if (res.reverted) {
        this.store.applyLocal(ref, [{ idx: last.idx, colour: res.colour }]);
        this.events.onInk(res.ink);
        return true;
      }
      await this.store.reload(ref);
      return false;
    } catch (err) {
      if (err instanceof ApiError) this.events.onError(err.code, err.message);
      return false;
    }
  }

  clearUndo(): void {
    this.undoStack = [];
  }

  /** Tile refs for the draw area (for subscribing). */
  areaRefs(): TileRef[] {
    return this.areaTiles().map((t) => tileFromToken(t));
  }

  areaKeys(): number[] {
    return this.areaRefs().map((r) => tileKey(r));
  }
}
